<script setup>
/* eslint-env browser */
/* global FB */
import { ref, computed, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  ensureFacebookSdkReady,
  primeFacebookSdk,
} from 'dashboard/helper/facebookSdk';
import { buildInstagramFacebookLoginOptions } from 'dashboard/helper/instagramFacebookLoginOptions';
import InboxReconnectionRequired from '../../components/InboxReconnectionRequired.vue';
import instagramClient from 'dashboard/api/channel/instagramClient';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');

const isRequestingAuthorization = ref(false);

onMounted(() => {
  primeFacebookSdk();
});

const isCommentsChannel = computed(() => {
  return props.inbox.channel_type === INBOX_TYPES.INSTAGRAM_COMMENTS;
});

const isFacebookLogin = computed(() => {
  return props.inbox.auth_method === 'facebook_login';
});

const facebookLoginOptions = computed(() =>
  buildInstagramFacebookLoginOptions(globalConfig.value, {
    isCommentsChannel: isCommentsChannel.value,
  })
);

async function requestInstagramAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    const payload = isCommentsChannel.value ? { channel_type: 'comments' } : {};
    const response = await instagramClient.generateAuthorization(payload);

    const {
      data: { url },
    } = response;

    window.location.href = url;
  } catch (error) {
    useAlert(
      isCommentsChannel.value
        ? t('INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.ERROR_AUTH')
        : t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH')
    );
  } finally {
    isRequestingAuthorization.value = false;
  }
}

async function requestFacebookAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    await ensureFacebookSdkReady();

    FB.login(async response => {
      if (response.status !== 'connected') {
        useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        isRequestingAuthorization.value = false;
        return;
      }

      try {
        await instagramClient.reauthorizeFacebookPage({
          omniauthToken: response.authResponse.accessToken,
          inboxId: props.inbox.id,
        });
        window.location.reload();
      } catch (error) {
        useAlert(
          isCommentsChannel.value
            ? t('INBOX_MGMT.ADD.INSTAGRAM_COMMENTS.ERROR_AUTH')
            : t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH')
        );
      } finally {
        isRequestingAuthorization.value = false;
      }
    }, facebookLoginOptions.value);
  } catch (error) {
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
    isRequestingAuthorization.value = false;
  }
}

function requestAuthorization() {
  if (isFacebookLogin.value) {
    requestFacebookAuthorization();
    return;
  }

  requestInstagramAuthorization();
}
</script>

<template>
  <InboxReconnectionRequired
    class="mx-8 mt-5"
    @reauthorize="requestAuthorization"
  />
</template>
